import { motion } from "framer-motion";
import {
  Dice5,
  ShoppingBag,
  Cpu,
  CandlestickChart,
  ShieldAlert,
} from "lucide-react";
import Navbar from "../components/neptune/Navbar";
import Footer from "../components/neptune/Footer";
import PageHeader from "../components/neptune/PageHeader";
import FinalCTA from "../components/neptune/FinalCTA";
import AnimatedIcon from "../components/neptune/AnimatedIcon";
import { useContent } from "../context/ContentContext";

const ICONS = {
  iGaming: Dice5,
  Ecommerce: ShoppingBag,
  SaaS: Cpu,
  Forex: CandlestickChart,
  "High-risk": ShieldAlert,
};

const fallbackIndustries = [
  {
    Icon: Dice5,
    tag: "iGaming",
    title: "Built for the speed of play.",
    body: "Real-time deposits and withdrawals, regulated MIDs, KYC orchestration and chargeback control — engineered for licensed gaming operators across regulated markets.",
    metrics: [
      ["+18%", "Deposit approval lift"],
      ["<3s", "Avg withdrawal latency"],
      ["24/7", "Operations support"],
    ],
  },
  {
    Icon: ShoppingBag,
    tag: "Ecommerce",
    title: "Convert at the checkout.",
    body: "Local methods, smart retries and one-click flows that move global shoppers from cart to conversion — without compromising on fraud control.",
    metrics: [
      ["+12%", "Checkout conversion"],
      ["80+", "Local payment methods"],
      ["1-Click", "Returning shoppers"],
    ],
  },
  {
    Icon: Cpu,
    tag: "SaaS",
    title: "Subscriptions that don't churn.",
    body: "Intelligent retry logic, dunning, account updater and usage-based billing — built for high-MRR teams who can't afford preventable churn.",
    metrics: [
      ["-38%", "Involuntary churn"],
      ["98.7%", "Recurring approval"],
      ["Any model", "Flat · seat · usage"],
    ],
  },
  {
    Icon: CandlestickChart,
    tag: "Forex",
    title: "Funding for global traders.",
    body: "High-volume cards, wire and crypto on-ramps with FX-aware routing. Onboarding, compliance and settlement designed for the realities of FX brokers.",
    metrics: [
      ["T+0", "Settlement"],
      ["Multi-MID", "Risk diversification"],
      ["Global", "Crypto on-ramps"],
    ],
  },
  {
    Icon: ShieldAlert,
    tag: "High-risk",
    title: "Where others say no.",
    body: "Specialist underwriting and routing for high-risk verticals. We help mature businesses access stable acquiring without compromising operational standards.",
    metrics: [
      ["Specialist", "Underwriting"],
      ["Diversified", "Acquirer mix"],
      ["Resilient", "Failover routing"],
    ],
  },
];

export default function Industries() {
  const { t } = useContent();
  const industries = t("industries.items", fallbackIndustries);
  return (
    <main
      data-testid="industries-page"
      className="relative min-h-screen bg-[#0a0a0a] text-white font-body overflow-x-hidden"
    >
      <Navbar />

      <PageHeader
        eyebrow={t("industries.header.eyebrow", "Industries")}
        title={t("industries.header.title", "Payments engineered for your vertical.")}
        sub={t("industries.header.sub", "From checkout-driven ecommerce to high-velocity gaming — Neptune is calibrated to the operational reality of your industry.")}
      />

      <section className="relative py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 divide-y divide-white/[0.06] border-y border-white/[0.06]">
          {industries.map((ind, i) => {
            const Icon = ICONS[ind.tag] || ShoppingBag;
            return (
            <motion.article
              key={ind.tag || i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              data-testid={`industry-${(ind.tag || "").toLowerCase()}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 gap-x-12 py-16 sm:py-24"
            >
              <div className="lg:col-span-4">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-4 inline-flex items-center gap-3">
                  <AnimatedIcon
                    Icon={Icon}
                    size="h-12 w-12"
                    iconClass="h-5 w-5"
                    testId={`industry-icon-${(ind.tag || "").toLowerCase()}`}
                  />
                  <span className="font-display text-xl text-white">
                    {ind.tag}
                  </span>
                </div>
              </div>
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl sm:text-5xl lg:text-[60px] lg:leading-[1.04] font-semibold tracking-tighter text-white">
                  {ind.title}
                </h2>
                <p className="mt-6 text-[15.5px] sm:text-[17px] text-zinc-400 leading-relaxed max-w-2xl">
                  {ind.body}
                </p>
                <div className="mt-9 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl">
                  {(ind.metrics || []).map(([v, l]) => (
                    <div key={l}>
                      <div className="font-display text-2xl sm:text-3xl font-semibold tracking-tight chrome-text">
                        {v}
                      </div>
                      <div className="mt-1.5 text-[12px] text-zinc-500 leading-snug">
                        {l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.article>
            );
          })}
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
}
